import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  ArrowRight,
  Trophy,
  Target,
  TrendingUp,
  Calendar,
  Dumbbell
} from 'lucide-react'
import Seo from '../components/Seo'
import { coachingPricing } from '../lib/coachingPrograms'
import { EnrollLink, tierKey } from '../components/ui/EnrollLink'

const OnlineTraining: React.FC = () => {
  const programs = [
    {
      id: 'champions-blueprint',
      packageBase: 'championsBlueprint',
      eyebrow: 'Competition Prep',
      name: "Champion's Blueprint™",
      pricing: coachingPricing.championsBlueprint,
      icon: Trophy,
      description:
        'Individualized contest-prep coaching for women preparing to step onstage, from initial planning through peak week.',
      features: [
        'Contest selection and prep planning',
        'Customized training programming',
        'Individualized nutrition strategy',
        'Cardio programming and adjustments',
        'Supplement guidance when appropriate',
        'Physique assessments and progress reviews',
        'Posing and stage-presentation guidance',
        'Peak-week strategy',
        'Competition-day guidance',
        'Post-show evaluation'
      ]
    },
    {
      id: 'pro-look',
      packageBase: 'proLook',
      eyebrow: 'Advanced Physique / Event Goal',
      name: 'The Pro Look™',
      pricing: coachingPricing.proLook,
      icon: Target,
      description:
        'Advanced physique coaching for a specific aesthetic goal, photoshoot, event, or next level of development—without assuming competition prep is the right approach.',
      features: [
        'Individualized physique assessment',
        'Customized resistance-training program',
        'Personalized nutrition strategy',
        'Cardio programming when appropriate',
        'Progress reviews and plan adjustments',
        'Recovery and conditioning guidance',
        'Structured timeline toward your defined goal',
        'Direct coaching support throughout the process'
      ]
    },
    {
      id: 'elite-competitive-edge',
      packageBase: 'eliteCompetitiveEdge',
      eyebrow: 'Offseason Development',
      name: 'Elite Competitive Edge™',
      pricing: coachingPricing.eliteCompetitiveEdge,
      icon: TrendingUp,
      description:
        "Purposeful offseason coaching focused on building muscle, refining your physique, and preparing for your next competitive goal.",
      features: [
        'Reverse-diet guidance when appropriate',
        'Offseason nutrition strategy',
        'Customized training programming',
        'Muscle-development priorities',
        'Cardio adjustments when appropriate',
        'Recovery planning',
        'Progress assessments',
        "Structured coaching toward your next physique goal"
      ]
    }
  ]

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Physique & Competition Coaching — Fit by Cassie"
        description="Individualized coaching for competition preparation, advanced physique development, offseason growth, and specific aesthetic or event goals."
        image="/images/cassie-online-training.webp"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-cream px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div
          className="absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-brand-teal/10 blur-3xl pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-brand-blush/15 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                Advanced Coaching
              </span>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-brand-navy mb-6">
                Physique &amp;{' '}
                <em className="block italic font-normal text-brand-teal mt-1">
                  Competition Coaching
                </em>
              </h1>

              <p className="font-display text-xl sm:text-2xl text-brand-navy/85 leading-relaxed mb-5">
                For women ready to pursue a more defined physique goal.
              </p>

              <p className="text-base sm:text-lg text-brand-navy/70 leading-relaxed max-w-xl mb-8">
                Individualized coaching for competition preparation, advanced
                physique development, offseason growth, and specific
                aesthetic or event goals. Your coaching plan is built around
                your experience, your starting point, and the outcome you're
                working toward.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <Link
                  to="/book-consultation"
                  className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                >
                  Schedule Your Complimentary Consultation
                  <Calendar className="ml-2" size={18} />
                </Link>

                <a
                  href="#programs"
                  className="inline-flex items-center justify-center rounded-full border border-brand-navy/25 bg-brand-ivory text-brand-navy font-semibold px-8 py-4 transition-colors hover:bg-white"
                >
                  Explore Coaching Options
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {[
                  'Individualized Plans',
                  'Physique-Focused Strategy',
                  'Coaching & Accountability'
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-2 text-sm font-semibold text-brand-navy/70"
                  >
                    <CheckCircle size={15} className="text-brand-teal" />
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] border border-brand-bronze/20" />

              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg bg-brand-ivory">
                <img
                  src="/images/cassie-online-training.webp"
                  alt="Cassie representing advanced physique and competition coaching"
                  className="w-full h-full object-cover object-[center_30%] aspect-[4/5] sm:aspect-[3/4]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="bg-brand-ivory px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Coaching With a Defined Goal
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
              Your goal determines{' '}
              <em className="italic font-normal text-brand-teal">
                the coaching path.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/70 leading-relaxed">
              Competition preparation, advanced physique goals, and offseason
              development each call for a different approach. During your
              complimentary consultation, we'll talk through your goals,
              experience, and timeline to determine the coaching path and
              level of support that fit you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section
        id="programs"
        className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24"
      >
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Physique &amp; Competition Coaching
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy mb-5">
              Three paths.{' '}
              <em className="italic font-normal text-brand-teal">
                One individualized approach.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed">
              Every coaching relationship begins with a complimentary
              consultation so we can determine which path and duration fit your
              goal.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 items-stretch">
            {programs.map((program, index) => {
              const Icon = program.icon

              return (
                <motion.article
                  key={program.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex flex-col bg-brand-ivory rounded-3xl border border-brand-bronze/20 shadow-soft p-7 sm:p-8"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-teal/10 flex items-center justify-center mb-6">
                    <Icon size={22} className="text-brand-teal" />
                  </div>

                  <span className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-3">
                    {program.eyebrow}
                  </span>

                  <h3 className="font-display text-2xl sm:text-3xl font-semibold text-brand-navy mb-4">
                    {program.name}
                  </h3>

                  <p className="text-brand-navy/65 leading-relaxed mb-7">
                    {program.description}
                  </p>

                  <div
                    className="overflow-hidden rounded-2xl border border-brand-teal/15 bg-brand-cream mb-7"
                    aria-label={`${program.name} founding pricing`}
                  >
                    <div className="px-5 py-3 border-b border-brand-teal/10">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-brand-teal">
                        Founding Pricing
                      </p>
                    </div>

                    {program.pricing.map((option, optionIndex) => (
                      <div
                        key={option.label}
                        className={`flex items-center justify-between gap-4 px-5 py-4 ${
                          optionIndex > 0
                            ? 'border-t border-brand-teal/10'
                            : ''
                        }`}
                      >
                        <div>
                          <p className="text-sm font-semibold text-brand-navy">
                            {option.label}
                          </p>

                          {option.detail && (
                            <p className="text-xs text-brand-navy/50 mt-1">
                              {option.detail}
                            </p>
                          )}

                          <p className="font-display text-xl font-semibold text-brand-navy whitespace-nowrap mt-1">
                            {option.price}
                          </p>
                        </div>

                        <EnrollLink
                          packageKey={tierKey(program.packageBase, option.length?.value)}
                        />
                      </div>
                    ))}
                  </div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {program.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle
                          size={16}
                          className="text-brand-teal shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-brand-navy/70 leading-relaxed">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/book-consultation"
                    className="inline-flex w-full items-center justify-center rounded-full border-2 border-brand-navy text-brand-navy font-bold px-6 py-4 transition-all duration-300 hover:bg-brand-navy hover:text-brand-cream"
                  >
                    Questions First? Schedule a Consultation
                    <ArrowRight className="ml-2" size={17} />
                  </Link>
                </motion.article>
              )
            })}
          </div>

          <p className="text-center text-sm text-brand-navy/50 mt-8">
            Consultation → coaching recommendation → enrollment
          </p>
        </div>
      </section>

      {/* COACHING APPROACH */}
      <section className="relative overflow-hidden bg-brand-navy px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <img
          src="/images/hero-gym-bg.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-brand-navy/90" />

        <div className="relative container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="rounded-3xl overflow-hidden shadow-soft-lg">
                <img
                  src="/images/online-workout-home.webp"
                  alt="Individualized coaching and physique development"
                  className="w-full h-full object-cover object-[center_30%] aspect-[4/5] sm:aspect-[3/4]"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-4">
                Individualized Coaching
              </span>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-ivory leading-tight mb-6">
                Advanced goals require{' '}
                <em className="italic font-normal text-brand-blush">
                  more than a template.
                </em>
              </h2>

              <p className="text-lg text-brand-cream/70 leading-relaxed mb-8">
                Your coaching plan is built around your goal, your
                experience, and your starting point. As you progress, I'll
                review how you're responding and adjust your training,
                nutrition, cardio when appropriate, and recovery strategy to
                keep your plan aligned with what you're working toward.
              </p>

              <div className="space-y-4">
                {[
                  'Individualized programming',
                  'Regular progress assessment',
                  'Goal-specific adjustments',
                  'Training and nutrition integration',
                  'Direct coaching support'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle
                      size={18}
                      className="text-brand-blush shrink-0"
                    />
                    <span className="text-brand-cream/85">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-brand-cream/60 text-sm leading-relaxed mt-6">
                Coaching is primarily online. You'll follow your
                individualized plan in your own training space, with
                guidance, progress reviews, and support from me along the
                way.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <div className="w-12 h-12 rounded-full bg-brand-teal/10 flex items-center justify-center mx-auto mb-6">
              <Dumbbell size={21} className="text-brand-teal" />
            </div>

            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
              Your Next Step
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-6">
              Start with the goal.{' '}
              <em className="italic font-normal text-brand-teal">
                Build the right path from there.
              </em>
            </h2>

            <p className="text-lg text-brand-navy/65 leading-relaxed mb-9 max-w-2xl mx-auto">
              You don't need to know which program or timeline is right for
              you before we talk. Tell me what you're working toward, where
              you're starting, and what you've tried before. We'll talk
              through your options and find the coaching path that makes
              sense for your goal.
            </p>

            <Link
              to="/book-consultation"
              className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-9 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
            >
              Schedule Your Complimentary Consultation
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  )
}

export default OnlineTraining
